import { CatalogService } from '../services/CatalogService';
import { setDynamicProfile, getProfile } from '../config/profiles';
import { isSupabaseConfigured } from '../services/api';
import type { Vendor } from '../types/drive';
import type { CatalogEntry } from '../types/catalog';
import {
  escapeHtml,
  modelItemHtml,
  renderCatalogListHtml,
  renderSelectModelButtonHtml,
  renderSpecsCard,
  vendorTabHtml,
} from './CatalogTemplates';
import { handleExportAll, handleFileUpload, handleMigrateToCloud } from './catalogActions';

export interface CatalogSelectorCallbacks {
  onProfileChange: (profileKey: string) => void;
}

export class CatalogSelector {
  private container: HTMLElement;
  private catalogService: CatalogService;
  private callbacks: CatalogSelectorCallbacks;
  private selectedVendor: Vendor | 'ALL' = 'ALL';
  private searchQuery = '';
  private selectedModelCode: string | null = null;
  private showManager = false;

  constructor(
    container: HTMLElement,
    catalogService: CatalogService,
    callbacks: CatalogSelectorCallbacks,
  ) {
    this.container = container;
    this.container.className = 'catalog-selector';
    this.catalogService = catalogService;
    this.callbacks = callbacks;
    this.render();
    this.catalogService.subscribe(() => this.render());
  }

  private render(): void {
    const models = this.catalogService.hasModels()
      ? this.getFilteredModels()
      : [];

    const vendors = this.catalogService.hasModels()
      ? this.catalogService.getVendors()
      : [];

    this.container.innerHTML = `
      <div class="catalog-selector-header">
        <label class="control-label">Modelo do Inversor</label>
        <div class="catalog-actions">
          <button id="btnImportCatalog" class="btn btn-sm btn-primary">+ Importar CSV</button>
          <button id="btnImportJSON" class="btn btn-sm btn-primary">+ Importar JSON</button>
          <button id="btnManageCatalogs" class="btn btn-sm btn-secondary">Gerenciar</button>
        </div>
      </div>

      <div id="catalogManagerPanel" class="catalog-manager-panel ${this.showManager ? '' : 'hidden'}">
        <div id="catalogUploadArea" class="catalog-upload-area">
          <input type="file" id="catalogFileInput" accept=".csv,.json" style="display:none">
          <div class="catalog-upload-placeholder">Solte CSV ou JSON aqui ou clique para selecionar</div>
        </div>
        <div id="catalogList" class="catalog-list">${this.renderCatalogList()}</div>
      </div>

      <div class="catalog-vendor-tabs">
        ${[...vendors.map(v => vendorTabHtml(v, this.selectedVendor)), vendorTabHtml('ALL', this.selectedVendor)].join('')}
      </div>

      <div class="catalog-search">
        <input type="text" id="catalogSearchInput" class="catalog-search-input" placeholder="Buscar por modelo, código ou família..." value="${escapeHtml(this.searchQuery)}">
      </div>

      <div id="catalogSelectActions" class="catalog-select-actions ${this.selectedModelCode ? '' : 'hidden'}">
        ${this.selectedModelCode ? renderSelectModelButtonHtml(this.catalogService.getModel(this.selectedModelCode)) : ''}
      </div>

      <div id="catalog-model-list" class="catalog-model-list">
        ${models.length === 0 ? '<div class="catalog-empty">Nenhum modelo encontrado</div>' : ''}
        ${models.map(m => modelItemHtml(m, this.selectedModelCode)).join('')}
      </div>

      <div id="catalogSpecsCard" class="catalog-specs-card ${this.selectedModelCode ? '' : 'hidden'}">
        ${this.selectedModelCode ? renderSpecsCard(this.catalogService.getModel(this.selectedModelCode)!) : ''}
      </div>
    `;

    this.attachEvents();
  }

  private getFilteredModels(): CatalogEntry[] {
    const filtered = this.catalogService.filter({
      vendor: this.selectedVendor === 'ALL' ? undefined : this.selectedVendor,
      search: this.searchQuery || undefined,
    });

    const seen = new Set<string>();
    const unique = filtered.filter(m => {
      if (seen.has(m.modelCode)) return false;
      seen.add(m.modelCode);
      return true;
    });

    return [...unique].sort(
      (a, b) =>
        a.vendor.localeCompare(b.vendor) ||
        a.family.localeCompare(b.family) ||
        a.nominalCurrent - b.nominalCurrent ||
        a.modelCode.localeCompare(b.modelCode),
    );
  }

  private refreshResults(): void {
    const models = this.catalogService.hasModels()
      ? this.getFilteredModels()
      : [];

    const listEl = this.container.querySelector('#catalog-model-list');
    if (listEl) {
      listEl.innerHTML =
        (models.length === 0
          ? '<div class="catalog-empty">Nenhum modelo encontrado</div>'
          : '') +
        models
          .map((m) => modelItemHtml(m, this.selectedModelCode))
          .join('');

      listEl.querySelectorAll('.catalog-model-item').forEach((item) => {
        item.addEventListener('click', () => {
          this.selectedModelCode = (item as HTMLElement).dataset.model ?? null;
          this.render();
        });
      });
    }

    const specsEl = this.container.querySelector('#catalogSpecsCard');
    if (specsEl) {
      specsEl.className = `catalog-specs-card ${this.selectedModelCode ? '' : 'hidden'}`;
      specsEl.innerHTML = this.selectedModelCode
        ? renderSpecsCard(this.catalogService.getModel(this.selectedModelCode)!)
        : '';
    }

    const actionsEl = this.container.querySelector('#catalogSelectActions');
    if (actionsEl) {
      actionsEl.className = `catalog-select-actions ${this.selectedModelCode ? '' : 'hidden'}`;
      actionsEl.innerHTML = renderSelectModelButtonHtml(
        this.selectedModelCode ? this.catalogService.getModel(this.selectedModelCode) : undefined,
      );
      const selectBtn = actionsEl.querySelector('#btnSelectModel');
      selectBtn?.addEventListener('click', () => {
        const modelCode = (selectBtn as HTMLElement).dataset.model;
        if (modelCode) {
          this.selectModel(modelCode);
        }
      });
    }
  }

  private attachEvents(): void {
    // Vendor tabs
    this.container.querySelectorAll('.catalog-tab').forEach(tab => {
      tab.addEventListener('click', () => {
        this.selectedVendor = (tab as HTMLElement).dataset.vendor as Vendor | 'ALL';
        this.selectedModelCode = null;
        this.render();
      });
    });

    // Search input
    const searchInput = this.container.querySelector('#catalogSearchInput') as HTMLInputElement;
    if (searchInput) {
      searchInput.addEventListener('input', () => {
        this.searchQuery = searchInput.value;
        this.selectedModelCode = null;
        this.refreshResults();
      });
    }

    // Model items
    this.container.querySelectorAll('.catalog-model-item').forEach(item => {
      item.addEventListener('click', () => {
        this.selectedModelCode = (item as HTMLElement).dataset.model ?? null;
        this.render();
      });
    });

    // Select model button (delegated)
    const selectBtn = this.container.querySelector('#btnSelectModel');
    if (selectBtn) {
      selectBtn.addEventListener('click', () => {
        const modelCode = (selectBtn as HTMLElement).dataset.model;
        if (modelCode) {
          this.selectModel(modelCode);
        }
      });
    }

    // Import CSV button
    const importBtn = this.container.querySelector('#btnImportCatalog');
    if (importBtn) {
      importBtn.addEventListener('click', () => {
        const fileInput = this.container.querySelector('#catalogFileInput') as HTMLInputElement;
        if (fileInput) {
          fileInput.accept = '.csv';
          fileInput.click();
        }
      });
    }

    // Import JSON button
    const importJsonBtn = this.container.querySelector('#btnImportJSON');
    if (importJsonBtn) {
      importJsonBtn.addEventListener('click', () => {
        const fileInput = this.container.querySelector('#catalogFileInput') as HTMLInputElement;
        if (fileInput) {
          fileInput.accept = '.json';
          fileInput.click();
        }
      });
    }

    // File input
    const fileInput = this.container.querySelector('#catalogFileInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.addEventListener('change', () => {
        if (fileInput.files && fileInput.files.length > 0) {
          handleFileUpload(this.catalogService, fileInput.files[0], () => this.render());
          fileInput.value = '';
        }
      });
    }

    // Drag and drop
    const uploadArea = this.container.querySelector('#catalogUploadArea');
    if (uploadArea) {
      uploadArea.addEventListener('dragover', (e: Event) => {
        (e as DragEvent).preventDefault();
        uploadArea.classList.add('drag-over');
      });
      uploadArea.addEventListener('dragleave', () => {
        uploadArea.classList.remove('drag-over');
      });
      uploadArea.addEventListener('drop', (e: Event) => {
        const de = e as DragEvent;
        de.preventDefault();
        uploadArea.classList.remove('drag-over');
        if (de.dataTransfer?.files.length) {
          handleFileUpload(this.catalogService, de.dataTransfer.files[0], () => this.render());
        }
      });
      uploadArea.addEventListener('click', () => {
        const fi = this.container.querySelector('#catalogFileInput') as HTMLInputElement;
        if (fi) fi.click();
      });
    }

    // Manage catalogs button
    const manageBtn = this.container.querySelector('#btnManageCatalogs');
    if (manageBtn) {
      manageBtn.addEventListener('click', () => {
        this.showManager = !this.showManager;
        this.render();
      });
    }

    // Remove catalog buttons
    this.container.querySelectorAll('.btn-remove-catalog').forEach(btn => {
      btn.addEventListener('click', () => {
        const key = (btn as HTMLElement).dataset.key;
        if (key && confirm('Remover este catálogo permanentemente?')) {
          this.catalogService.removeCatalog(key);
          if (this.selectedModelCode) {
            const stillExists = this.catalogService.getModel(this.selectedModelCode);
            if (!stillExists) this.selectedModelCode = null;
          }
          this.render();
        }
      });
    });

    // Export all catalogs
    const exportBtn = this.container.querySelector('#btnExportAllCatalogs');
    if (exportBtn) {
      exportBtn.addEventListener('click', () => handleExportAll(this.catalogService));
    }

    // Migrate to cloud
    const migrateBtn = this.container.querySelector('#btnMigrateToCloud');
    if (migrateBtn) {
      migrateBtn.addEventListener('click', () => handleMigrateToCloud(() => this.render()));
    }
  }

  private selectModel(modelCode: string): void {
    const entry = this.catalogService.getModel(modelCode);
    if (!entry) return;

    const profile = this.catalogService.generateProfile(entry);
    const key = profile.id;

    const existing = getProfile(key);
    if (!existing) {
      setDynamicProfile(key, profile);
    }

    this.callbacks.onProfileChange(key);

    this.searchQuery = '';
    this.selectedModelCode = null;
    this.render();
  }

  private renderCatalogList(): string {
    const catalogs = this.catalogService.getCatalogs();
    const items = catalogs
      .map(({ key, catalog }) => ({
        key,
        vendor: catalog.vendor,
        family: catalog.family,
        models: catalog.models.length,
      }))
      .sort(
        (a, b) =>
          a.vendor.localeCompare(b.vendor) ||
          a.family.localeCompare(b.family),
      );
    return renderCatalogListHtml(items, this.catalogService.getPendingOpCount(), isSupabaseConfigured());
  }
}
